import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  TextField,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Factory as FactoryIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../../components/common/dialog/BootstrapDialog';
import OutsourcePartnerSelectbox from '../../../components/common/form/autocomplete/OutsourcePartnerSelectbox';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as manufacturingApi from '../../../lib/api/manufacturing';

/**
 * 외주 설정 다이얼로그
 * - 공정을 외주로 전환하거나 외주 정보를 수정
 * - OutsourcePartnerSelectbox: partnerUuid + partnerNm 동시 저장
 */
const OutsourceDialog = ({
  open,
  onClose,
  processData,    // 대상 공정 라우팅 상세 데이터
  planId,
  outsourceData,  // 기존 외주 데이터 (수정 시)
  onSaved,        // 저장 완료 콜백
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEditMode = !!outsourceData?.outsourceUuid;

  const formik = useFormik({
    initialValues: {
      partnerUuid: '',
      partnerNm: '',
      contactNm: '',
      contactTel: '',
      orderDate: '',
      dueDate: '',
      remarks: '',
    },
    validationSchema: Yup.object({
      partnerUuid: Yup.string().required('외주업체를 선택해주세요'),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      setError(null);
      try {
        const payload = {
          routingDetUuid: processData?.routingDetUuid,
          planId,
          processNm: processData?.processNm,
          partnerUuid: values.partnerUuid,
          partnerNm: values.partnerNm,
          contactNm: values.contactNm || null,
          contactTel: values.contactTel || null,
          orderDate: values.orderDate || null,
          dueDate: values.dueDate || null,
          remarks: values.remarks || null,
          status: isEditMode ? outsourceData.status : 'ORDERED',
        };

        if (isEditMode) {
          await manufacturingApi.updateOutsource(outsourceData.outsourceUuid, payload);
        } else {
          await manufacturingApi.createOutsource(payload);
        }

        if (onSaved) onSaved();
        onClose();
      } catch (err) {
        console.error('외주 저장 실패:', err);
        setError(err.response?.data?.message || '저장에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    },
  });

  // 다이얼로그 열릴 때 초기값 설정
  useEffect(() => {
    if (open) {
      setError(null);
      if (isEditMode && outsourceData) {
        formik.setValues({
          partnerUuid: outsourceData.partnerUuid || '',
          partnerNm: outsourceData.partnerNm || '',
          contactNm: outsourceData.contactNm || '',
          contactTel: outsourceData.contactTel || '',
          orderDate: outsourceData.orderDate || '',
          dueDate: outsourceData.dueDate || '',
          remarks: outsourceData.remarks || '',
        });
      } else {
        formik.resetForm();
        formik.setFieldValue('orderDate', new Date().toISOString().split('T')[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, outsourceData]);

  if (!processData) return null;

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BootstrapDialogTitle onClose={onClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactoryIcon color="primary" />
          <Typography variant="h6">
            {isEditMode ? '외주 정보 수정' : '외주 설정'}
          </Typography>
          <Chip
            label={processData.processNm}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        </Box>
      </BootstrapDialogTitle>

      <DialogContent dividers>
        <Box component="form" sx={{ py: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 공정 정보 요약 */}
          <Box sx={{ bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1, mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              공정 순서 <strong>{processData.processSeq}</strong>
              &nbsp;|&nbsp;
              공정명 <strong>{processData.processNm}</strong>
              {processData.stdWorkTime && (
                <>&nbsp;|&nbsp;표준시간 <strong>{processData.stdWorkTime}h</strong></>
              )}
            </Typography>
          </Box>

          <Grid container spacing={2}>

            {/* 외주업체 - OutsourcePartnerSelectbox로 uuid + nm 동시 저장 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                🏭 외주업체 <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <OutsourcePartnerSelectbox
                formik={formik}
                uuidName="partnerUuid"
                nmName="partnerNm"
                label="외주업체 선택"
                variant="outlined"
                size="small"
                required
              />
            </Grid>

            {/* 담당자 정보 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                👤 담당자 정보
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="담당자명"
                name="contactNm"
                value={formik.values.contactNm}
                onChange={formik.handleChange}
                variant="outlined"
                size="small"
                placeholder="담당자 이름"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="연락처"
                name="contactTel"
                value={formik.values.contactTel}
                onChange={formik.handleChange}
                variant="outlined"
                size="small"
                placeholder="010-0000-0000"
              />
            </Grid>

            {/* 일정 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, mt: 1 }}>
                📅 일정
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="발주일"
                name="orderDate"
                type="date"
                value={formik.values.orderDate}
                onChange={formik.handleChange}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="납기 예정일"
                name="dueDate"
                type="date"
                value={formik.values.dueDate}
                onChange={formik.handleChange}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 비고 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="비고"
                name="remarks"
                value={formik.values.remarks}
                onChange={formik.handleChange}
                variant="outlined"
                size="small"
                multiline
                rows={2}
                placeholder="특이사항이 있으면 입력해주세요"
              />
            </Grid>

          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />} disabled={saving}>
          취소
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={saving}
        >
          {saving ? '저장중...' : isEditMode ? '수정' : '외주 등록'}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default OutsourceDialog;
